import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import {
  DocumentAnalysisItem,
  DocumentAnalysisItemsByScope,
  DocumentAnalysisList,
  documentAnalysisListSchema,
  isOrganizationAnalysisScope,
  normalizeAnalysisDocumentId,
} from '@/entities/DocumentAnalysis';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import {
  readExecutionStorageState,
  subscribeAnyExecutionStorage,
  readGlobalExecutionState,
  subscribeGlobalExecution,
  writeGlobalExecutionState,
  removeGlobalExecutionState,
  type GlobalExecutionState,
} from '@/utils/analysisExecutionStorage';
import api from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

type Key = string;

function makeKey(organizationId: string, documentId: string): Key {
  return `${organizationId}:${documentId}`;
}

type PrivateFields = '_isGlobalRunning' | '_isAnyRunning';

export class AnalysesStore implements ILocalStore {
  private _itemsMap = observable.map<Key, DocumentAnalysisItem[]>();
  private _totalsMap = observable.map<Key, number>();
  private _metaMap = observable.map<Key, MetaStore>();
  private _runningMap = observable.map<Key, Set<number>>();
  private _runningUnsub = new Map<Key, () => void>();
  private _idsMap = observable.map<Key, number[]>();
  private _idsKeyMap = observable.map<Key, string>();
  private _isGlobalRunning = false;
  private _isAnyRunning = false;
  private _globalUnsub: (() => void) | null = null;

  constructor() {
    makeObservable<AnalysesStore, PrivateFields>(this, {
      fetchAvailableAnalyses: action.bound,
      setAnalysisResultId: action.bound,
      setRunning: action.bound,
      setGlobalRunning: action.bound,
      _isGlobalRunning: observable,
      _isAnyRunning: observable,
      isAnyRunning: computed,
    });

    this._initGlobalSync();
  }

  private _normalize(orgId: string, docId?: string | null): { orgId: string; docId: string; key: Key } {
    const normalizedDocId = normalizeAnalysisDocumentId(docId);
    const key = makeKey(orgId, normalizedDocId);

    return { orgId, docId: normalizedDocId, key };
  }

  private ensureMeta(orgId: string, docId?: string | null): MetaStore {
    const { key } = this._normalize(orgId, docId);
    let meta = this._metaMap.get(key);

    if (!meta) {
      meta = new MetaStore();
      this._metaMap.set(key, meta);
    }

    return meta;
  }

  private setItems(orgId: string, docId: string | null | undefined, items: DocumentAnalysisItem[]): void {
    const { key } = this._normalize(orgId, docId);
    const ids = items.map((item) => item.id);

    this._itemsMap.set(key, items);
    this._idsMap.set(key, ids);
    this._idsKeyMap.set(key, ids.join(','));
  }

  private setTotal(orgId: string, docId: string | null | undefined, total: number): void {
    const { key } = this._normalize(orgId, docId);

    this._totalsMap.set(key, total);
  }

  private _groupItemsByScope = (items: DocumentAnalysisItem[]): DocumentAnalysisItemsByScope => {
    const grouped: DocumentAnalysisItemsByScope = {
      DOCUMENT: [],
      ORGANIZATION: [],
    };

    items.forEach((item) => {
      grouped[item.scope].push(item);
    });

    return grouped;
  };

  async fetchAvailableAnalyses(organizationId: string, documentId?: string | null): Promise<void> {
    const normalizedDocumentId = normalizeAnalysisDocumentId(documentId);
    const meta = this.ensureMeta(organizationId, normalizedDocumentId);

    if (meta.isLoading) {
      return;
    }

    meta.setLoadedStartMeta();

    const endpoint = isOrganizationAnalysisScope(documentId)
      ? ENDPOINTS.analyze.organizationAvailable(organizationId)
      : ENDPOINTS.analyze.available(organizationId, normalizedDocumentId);

    const response = await api<DocumentAnalysisList>({ endpoint });

    if (response.isError) {
      runInAction(() => {
        this.setItems(organizationId, normalizedDocumentId, []);
        this.setTotal(organizationId, normalizedDocumentId, 0);
        meta.setLoadedErrorMeta();
      });

      return;
    }

    const parsed = schemeParse(documentAnalysisListSchema, response.data);
    const items = parsed.success ? parsed.data.items : [];
    const total = parsed.success ? (parsed.data.total ?? items.length) : 0;

    runInAction(() => {
      this.setItems(organizationId, normalizedDocumentId, items);
      this.setTotal(organizationId, normalizedDocumentId, total);
      meta.setLoadedSuccessMeta();
    });
  }

  getAvailableAnalyses(organizationId: string, documentId?: string | null): DocumentAnalysisItemsByScope {
    const { key } = this._normalize(organizationId, documentId);
    const items = this._itemsMap.get(key) ?? [];

    return this._groupItemsByScope(items);
  }

  getTotal(organizationId: string, documentId?: string | null): number {
    const { key } = this._normalize(organizationId, documentId);

    return this._totalsMap.get(key) ?? 0;
  }

  getMeta(organizationId: string, documentId?: string | null): MetaStore {
    return this.ensureMeta(organizationId, documentId);
  }

  getAnalysisById(
    organizationId: string,
    documentId: string | null | undefined,
    analysisTypeId: number,
  ): DocumentAnalysisItem | undefined {
    const { key } = this._normalize(organizationId, documentId);
    const items = this._itemsMap.get(key) ?? [];

    return items.find((i) => i.id === analysisTypeId);
  }

  hasAvailableResult = (organizationId: string, documentId?: string | null): boolean => {
    const { key } = this._normalize(organizationId, documentId);
    const items = this._itemsMap.get(key) ?? [];

    return items.some((item) => item.analysis_result_id !== null);
  };

  setAnalysisResultId(
    organizationId: string,
    documentId: string | null | undefined,
    analysisTypeId: number,
    analysisResultId: number | null,
  ): void {
    const { key } = this._normalize(organizationId, documentId);
    const items = this._itemsMap.get(key);

    if (!items || items.length === 0) {
      return;
    }

    const index = items.findIndex((item) => item.id === analysisTypeId);

    if (index === -1) {
      return;
    }

    const currentItem = items[index];

    if (currentItem.analysis_result_id === analysisResultId) {
      return;
    }

    const nextItems = items.slice();

    nextItems[index] = {
      ...currentItem,
      analysis_result_id: analysisResultId,
    };

    this._itemsMap.set(key, nextItems);
  }

  setRunning(
    organizationId: string,
    documentId: string | null | undefined,
    analysisTypeId: number,
    isRunning: boolean,
  ): void {
    const normalized = this._normalize(organizationId, documentId);
    const current = this._runningMap.get(normalized.key) ?? new Set<number>();
    const next = new Set(current);

    if (isRunning) {
      next.add(analysisTypeId);
    } else {
      next.delete(analysisTypeId);
    }

    this._runningMap.set(normalized.key, next);
    if (!isRunning) {
      this._clearGlobalIfMatches(normalized.orgId, normalized.docId, analysisTypeId);
    }
    this._recalculateIsAnyRunning();
  }

  setGlobalRunning(params: {
    organizationId: string;
    documentId: string;
    analysisTypeId: number;
    isRunning: boolean;
  }): void {
    if (params.isRunning) {
      const payload: GlobalExecutionState = {
        status: 'running',
        organizationId: params.organizationId,
        documentId: params.documentId,
        analysisTypeId: params.analysisTypeId,
        updatedAt: Date.now(),
      };

      writeGlobalExecutionState(payload);
      this._isGlobalRunning = true;
      this._recalculateIsAnyRunning();

      return;
    }

    removeGlobalExecutionState();
    this._isGlobalRunning = false;
    this._recalculateIsAnyRunning();
  }

  destroy(): void {
    this._metaMap.forEach((m) => m.destroy());
    this._itemsMap.clear();
    this._totalsMap.clear();
    this._metaMap.clear();
    this._runningUnsub.forEach((fn) => fn());
    this._runningUnsub.clear();
    this._runningMap.clear();
    this._idsMap.clear();
    this._idsKeyMap.clear();
    this._isGlobalRunning = false;
    this._isAnyRunning = false;
    this._globalUnsub?.();
    this._globalUnsub = null;
  }

  getRunning(organizationId: string, documentId?: string | null): Set<number> {
    const { key, orgId, docId } = this._normalize(organizationId, documentId);
    const current = this._runningMap.get(key) ?? new Set<number>();

    // Defensive cleanup: remove ids whose storage state is no longer "running"
    // (covers rare cases when storage events were missed).
    const pruned = new Set<number>();

    current.forEach((id) => {
      const state = readExecutionStorageState(orgId, docId, id);

      if (state?.status === 'running') {
        pruned.add(id);
      }
    });

    if (pruned.size !== current.size) {
      this._runningMap.set(key, pruned);
      this._recalculateIsAnyRunning();
    }

    return pruned;
  }

  getAnalysisIds(organizationId: string, documentId?: string | null): { ids: number[]; key: string } {
    const { key } = this._normalize(organizationId, documentId);

    return {
      ids: this._idsMap.get(key) ?? [],
      key: this._idsKeyMap.get(key) ?? '',
    };
  }

  getActiveRunningId(organizationId: string, documentId?: string | null): number | null {
    const running = this.getRunning(organizationId, documentId);
    const first = running.values().next();

    return first.done ? null : first.value;
  }

  get isAnyRunning(): boolean {
    return this._isAnyRunning;
  }

  getBlockState(
    organizationId: string,
    documentId: string | null | undefined,
    analysisTypeId: number,
  ): {
    isDisabled: boolean;
    tooltip?: string;
  } {
    const activeRunningId = this.getActiveRunningId(organizationId, documentId);

    if (activeRunningId === null || activeRunningId === analysisTypeId) {
      return { isDisabled: false };
    }

    const target = this.getAnalysisById(organizationId, documentId, analysisTypeId);
    const hasResult = target?.analysis_result_id != null;

    if (hasResult) {
      return { isDisabled: false };
    }

    return { isDisabled: true, tooltip: 'Сначала дождитесь завершения текущего анализа' };
  }

  observeRunning(organizationId: string, documentId: string | null | undefined, analysisIds: number[]): () => void {
    const { key, docId } = this._normalize(organizationId, documentId);

    this._runningUnsub.get(key)?.();

    const next = new Set<number>();

    analysisIds.forEach((id) => {
      const state = readExecutionStorageState(organizationId, docId, id);

      if (state?.status === 'running') {
        next.add(id);
      }
    });

    runInAction(() => {
      this._runningMap.set(key, next);
      this._recalculateIsAnyRunning();
    });

    const unsubscribe = subscribeAnyExecutionStorage(({ key: storageKey, state }) => {
      if (storageKey.organizationId !== organizationId || storageKey.documentId !== docId) {
        return;
      }

      runInAction(() => {
        const current = this._runningMap.get(key) ?? new Set<number>();
        const updated = new Set(current);

        if (state?.status === 'running') {
          updated.add(storageKey.analysisTypeId);
        } else {
          updated.delete(storageKey.analysisTypeId);
        }

        this._runningMap.set(key, updated);
        this._recalculateIsAnyRunning();
      });
    });

    this._runningUnsub.set(key, unsubscribe);

    return () => {
      this._runningUnsub.get(key)?.();
      this._runningUnsub.delete(key);
    };
  }

  private _initGlobalSync(): void {
    const current = readGlobalExecutionState();

    this._isGlobalRunning = current?.status === 'running';

    this._globalUnsub = subscribeGlobalExecution((state) => {
      runInAction(() => {
        this._isGlobalRunning = state?.status === 'running';
        this._recalculateIsAnyRunning();
      });
    });

    this._recalculateIsAnyRunning();
  }

  private _recalculateIsAnyRunning(): void {
    const hasLocalRunning = Array.from(this._runningMap.values()).some((set) => set.size > 0);

    this._isAnyRunning = this._isGlobalRunning || hasLocalRunning;
  }

  private _clearGlobalIfMatches(organizationId: string, documentId: string, analysisTypeId: number): boolean {
    const global = readGlobalExecutionState();

    if (
      global?.status === 'running' &&
      global.organizationId === organizationId &&
      global.documentId === documentId &&
      global.analysisTypeId === analysisTypeId
    ) {
      removeGlobalExecutionState();
      this._isGlobalRunning = false;

      return true;
    }

    return false;
  }
}
